import {
  CalendarIssue,
  useGrippContext,
  useUpdateIssue,
} from '@gripp/shared-logic';
import { CalendarVisibleRange } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import Toast from 'react-native-toast-message';

/** luxon DateTime, borrowed from the Calendar's drop callback type (no luxon import). */
type LuxonDateTime = CalendarVisibleRange['start'];

/** Raw shape the `useActivityCalendar` query caches before its `select` maps it. */
interface CalendarQueryData {
  issue?: { findIssues?: { items?: CalendarIssue[] } };
}

/**
 * Drag-to-reschedule write path (plan 0017 §3, P3.S2). Returns a handler for the
 * Calendar's `onEventDrop(eventId, newDate)`:
 *
 *  - **Optimistic move:** rewrites the dragged issue's `effectiveCreationDate` in
 *    every cached `useActivityCalendar` window (matched by the `['issue','calendar']`
 *    key prefix, so we don't have to reconstruct the exact window+filter key) so
 *    the pill jumps to the new day immediately. A snapshot is taken for rollback.
 *  - **Persist:** `useUpdateIssue({ input: { id, adjustCreationDate } })`. The field
 *    is the `DateTime` scalar — sent as a luxon ISO string (`newDate.toISO()`), the
 *    same field `AdjustCreationDateField` writes from the edit modal.
 *  - **Settle:** `useUpdateIssue`'s own `onSuccess` already invalidates every
 *    `['issue', …]` query (calendar windows included) so server truth wins; we also
 *    invalidate the calendar prefix in `onSettled` as a belt-and-suspenders.
 *  - **Rollback + surface:** on error, restore the snapshot and show a toast + log
 *    via the GrippContext logger (never a bare `void`).
 */
export const useRescheduleActivity = () => {
  const queryClient = useQueryClient();
  const { mutate: updateIssue } = useUpdateIssue();
  const { logger } = useGrippContext();

  return useCallback(
    (eventId: string, newDate: LuxonDateTime) => {
      // DateTime is `any` via the shared-ui luxon shim; `.toISO()` is safe here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newIso = (newDate as any).toISO() as string;

      const calendarFilter = { queryKey: ['issue', 'calendar'] as const };

      // Snapshot + optimistic update across all matching calendar windows.
      const previous = queryClient.getQueriesData<CalendarQueryData>(
        calendarFilter
      );

      queryClient.setQueriesData<CalendarQueryData>(calendarFilter, (data) => {
        const items = data?.issue?.findIssues?.items;
        if (!items) return data;
        return {
          ...data,
          issue: {
            ...data?.issue,
            findIssues: {
              ...data?.issue?.findIssues,
              items: items.map((it) =>
                it.id === eventId
                  ? { ...it, effectiveCreationDate: newIso }
                  : it
              ),
            },
          },
        };
      });

      updateIssue(
        { input: { id: eventId, adjustCreationDate: newIso } },
        {
          onError: (error) => {
            // Roll back every window we optimistically touched.
            previous.forEach(([key, snapshot]) => {
              queryClient.setQueryData(key, snapshot);
            });
            logger.error(
              `Failed to reschedule activity ${eventId}: ${String(error)}`
            );
            Toast.show({
              type: 'error',
              text1: 'Could not move activity',
              text2: 'The change was not saved. Please try again.',
              position: 'bottom',
              autoHide: true,
              visibilityTime: 3000,
            });
          },
          onSettled: () => {
            queryClient
              .invalidateQueries({ queryKey: ['issue', 'calendar'] })
              .catch(logger.error);
          },
        }
      );
    },
    [queryClient, updateIssue, logger]
  );
};
